import type { SaleInstalment } from "../api/SalesAPI";

export const INSTALMENT_SUM_EPS = 0.01;

export type TemplateInstalmentRow = { percentage: number; method: string };

export function mapTemplateMethod(method: string | undefined): "CARD" | "TRANSFER" | "CASH" {
  const methodLower = (method || "").toLowerCase();
  if (methodLower === "card") return "CARD";
  if (methodLower === "transfer") return "TRANSFER";
  if (methodLower === "cash") return "CASH";
  return "CASH";
}

function evenSplitWithRemainderOnFirstSlot(total: number, parts: number): number[] {
  if (parts <= 0) return [];
  const per = parseFloat((total / parts).toFixed(2));
  const remainder = parseFloat((total - per * parts).toFixed(2));
  return Array.from({ length: parts }, (_, i) =>
    parseFloat((i === 0 ? per + remainder : per).toFixed(2))
  );
}

/** Percentage-based amounts; corrects rounding drift on the last slice. */
function amountsFromTemplate(totalPrice: number, template: TemplateInstalmentRow[]): number[] {
  const raw = template.map((row) => {
    const pct =
      typeof row.percentage === "number" ? row.percentage : parseFloat(String(row.percentage)) || 0;
    return parseFloat(((totalPrice * pct) / 100).toFixed(2));
  });
  const sum = raw.reduce((a, b) => a + b, 0);
  const drift = parseFloat((totalPrice - sum).toFixed(2));
  if (Math.abs(drift) >= INSTALMENT_SUM_EPS && raw.length > 0) {
    raw[raw.length - 1] = parseFloat((raw[raw.length - 1]! + drift).toFixed(2));
  }
  return raw;
}

export function sumInstalmentAmounts(instalments: SaleInstalment[]): number {
  return instalments.reduce((s, inst) => s + (inst.amount || 0), 0);
}

/**
 * Fresh instalment rows for `count` slots (no paid / metadata). Used by tests and as building block.
 */
export function suggestFullSplit(
  totalPrice: number,
  count: number,
  templateInstalments: TemplateInstalmentRow[] | undefined,
  currency: string
): Pick<SaleInstalment, "amount" | "currency" | "paymentMethod">[] {
  if (count < 1 || totalPrice <= 0) return [];
  if (templateInstalments && templateInstalments.length === count) {
    const amounts = amountsFromTemplate(totalPrice, templateInstalments);
    return templateInstalments.map((row, i) => ({
      amount: amounts[i]!,
      currency,
      paymentMethod: mapTemplateMethod(row.method),
    }));
  }
  const amounts = evenSplitWithRemainderOnFirstSlot(totalPrice, count);
  return amounts.map((amount) => ({
    amount,
    currency,
    paymentMethod: "TRANSFER" as const,
  }));
}

/**
 * Recomputes amounts for **unpaid** instalments so they sum to `totalPrice - sum(paid)`.
 * Paid rows are unchanged.
 *
 * If some unpaid rows already have a non-zero amount (“introduced”) and others are still ~empty,
 * only the empty rows get the **remaining** total (e.g. 4000 + empty with total 5000 → suggest 1000 on the empty one).
 * If every unpaid row is still empty, uses template (when `template.length === unpaidCount`) or an even split across all unpaid.
 */
export function suggestFullSplitRespectingPaid(
  totalPrice: number,
  current: SaleInstalment[],
  templateInstalments: TemplateInstalmentRow[] | undefined
): SaleInstalment[] | null {
  if (totalPrice <= 0 || current.length === 0) return null;
  const paidSum = current.reduce((s, inst) => s + (inst.isPaid ? inst.amount || 0 : 0), 0);
  const unpaidIndices = current.map((inst, i) => (!inst.isPaid ? i : -1)).filter((i): i is number => i >= 0);
  if (unpaidIndices.length === 0) return null;
  const unpaidTarget = parseFloat((totalPrice - paidSum).toFixed(2));
  if (unpaidTarget < -INSTALMENT_SUM_EPS) return null;

  const anchoredUnpaid = unpaidIndices.filter((i) => (current[i]!.amount || 0) > INSTALMENT_SUM_EPS);
  const flexibleUnpaid = unpaidIndices.filter((i) => (current[i]!.amount || 0) <= INSTALMENT_SUM_EPS);
  const hasIntroducedUnpaid = anchoredUnpaid.length > 0;

  const tpl = templateInstalments;

  if (flexibleUnpaid.length > 0 && hasIntroducedUnpaid) {
    const lockedSum = anchoredUnpaid.reduce((s, i) => s + (current[i]!.amount || 0), 0);
    const remainder = parseFloat((unpaidTarget - lockedSum).toFixed(2));
    if (remainder < -INSTALMENT_SUM_EPS) return null;
    const flexSorted = [...flexibleUnpaid].sort((a, b) => a - b);
    const flexAmounts = evenSplitWithRemainderOnFirstSlot(remainder, flexSorted.length);

    return current.map((inst, i) => {
      if (inst.isPaid) return { ...inst };
      if (!unpaidIndices.includes(i)) return { ...inst };
      if (anchoredUnpaid.includes(i)) {
        return { ...inst };
      }
      const fi = flexSorted.indexOf(i);
      const newAmount = flexAmounts[fi]!;
      const paymentMethod =
        tpl && tpl.length === unpaidIndices.length
          ? mapTemplateMethod(tpl[unpaidIndices.indexOf(i)]!.method)
          : inst.paymentMethod || "TRANSFER";
      return {
        ...inst,
        amount: newAmount,
        paymentMethod,
      };
    });
  }

  let unpaidAmounts: number[];
  let unpaidMethods: Array<"CARD" | "TRANSFER" | "CASH">;

  if (tpl && tpl.length === unpaidIndices.length) {
    unpaidAmounts = amountsFromTemplate(unpaidTarget, tpl);
    unpaidMethods = tpl.map((row) => mapTemplateMethod(row.method));
  } else {
    unpaidAmounts = evenSplitWithRemainderOnFirstSlot(unpaidTarget, unpaidIndices.length);
    unpaidMethods = Array.from({ length: unpaidIndices.length }, () => "TRANSFER" as const);
  }

  return current.map((inst, i) => {
    const ui = unpaidIndices.indexOf(i);
    if (inst.isPaid) return { ...inst };
    if (ui < 0) return { ...inst };
    return {
      ...inst,
      amount: unpaidAmounts[ui]!,
      paymentMethod: unpaidMethods[ui]!,
    };
  });
}

/**
 * Silently realigns instalment amounts when `totalPrice` changes (e.g. discount applied).
 * Returns `null` when instalments already sum to the total or resync is impossible.
 */
export function resyncInstalmentAmountsForTotal(
  totalPrice: number,
  instalments: SaleInstalment[],
  templateInstalments?: TemplateInstalmentRow[]
): SaleInstalment[] | null {
  if (totalPrice <= 0 || instalments.length === 0) return null;
  if (Math.abs(sumInstalmentAmounts(instalments) - totalPrice) < INSTALMENT_SUM_EPS) {
    return null;
  }
  const tpl =
    templateInstalments && templateInstalments.length > 0 ? templateInstalments : undefined;
  return suggestFullSplitRespectingPaid(totalPrice, instalments, tpl);
}

/**
 * Keeps paid rows and the anchor row fixed; splits remaining total evenly across other unpaid rows.
 */
export function suggestRebalanceToTotal(
  totalPrice: number,
  current: SaleInstalment[],
  anchorIndex: number
): SaleInstalment[] | null {
  if (totalPrice <= 0 || anchorIndex < 0 || anchorIndex >= current.length) return null;
  const anchor = current[anchorIndex];
  if (!anchor || anchor.isPaid) return null;

  const paidSum = current.reduce((s, inst) => s + (inst.isPaid ? inst.amount || 0 : 0), 0);
  const anchorAmount = anchor.amount || 0;
  const adjustableIndices = current
    .map((inst, i) => (!inst.isPaid && i !== anchorIndex ? i : -1))
    .filter((i): i is number => i >= 0)
    .sort((a, b) => a - b);

  const fixedSum = parseFloat((paidSum + anchorAmount).toFixed(2));
  const remainder = parseFloat((totalPrice - fixedSum).toFixed(2));
  if (remainder < -INSTALMENT_SUM_EPS) return null;
  if (adjustableIndices.length === 0) {
    if (Math.abs(remainder) < INSTALMENT_SUM_EPS) return current.map((inst) => ({ ...inst }));
    return null;
  }

  const split = evenSplitWithRemainderOnFirstSlot(remainder, adjustableIndices.length);

  return current.map((inst, i) => {
    if (inst.isPaid) return { ...inst };
    if (i === anchorIndex) return { ...inst };
    const ai = adjustableIndices.indexOf(i);
    if (ai < 0) return { ...inst };
    return {
      ...inst,
      amount: split[ai]!,
    };
  });
}
